import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Tenants } from '@/lib/api/v1';
import { Auth } from '@/lib/api';
import { Button, Card, EmptyState, Icon, Spinner } from '@/design-system';
import { Logo } from '@/components/Logo';
import { useSession, useToasts, usePrefs } from '@/lib/store';

/**
 * Lands at /invite/:token — pulled from the link emailed by the inviter.
 *
 * Flow:
 *  1. Already signed in → POST /invitations/:token/accept → /cellar
 *  2. Not signed in     → ask user to enter their email and continue;
 *                          when they verify a code, the invite is auto-accepted.
 */
export function InviteAcceptPage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { user, memberships, setSession } = useSession();
  const { setActiveTenantId } = usePrefs();
  const { push } = useToasts();
  const [phase, setPhase] = useState<'idle' | 'accepting' | 'done' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const accept = useMutation({
    mutationFn: () => Tenants.acceptInvite(token),
    onSuccess: (data) => {
      const next = [...memberships.filter((m) => m.tenantId !== data.membership.tenantId), data.membership];
      setSession({ user: useSession.getState().user, memberships: next });
      setActiveTenantId(data.tenant.id);
      setPhase('done');
      push({ title: 'Invitasjon akseptert', description: data.tenant.name, tone: 'success' });
      setTimeout(() => navigate('/cellar', { replace: true }), 600);
    },
    onError: (err: Error) => {
      setPhase('error');
      setErrorMsg(err.message);
    },
  });

  // Auto-accept once the user is signed in.
  useEffect(() => {
    if (user && phase === 'idle' && token) {
      setPhase('accepting');
      accept.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  return (
    <div className="relative grid min-h-screen place-items-center px-4">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 left-1/4 h-96 w-96 rounded-full bg-candle/15 blur-3xl animate-flicker" />
        <div className="absolute right-0 bottom-0 h-[28rem] w-[28rem] rounded-full bg-ruby/15 blur-3xl" />
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-md">
        <Card glow className="grid gap-6 p-8">
          <div className="flex flex-col items-center gap-3 text-center">
            <Logo size={40} showWordmark={false} />
            <h1 className="font-serif text-3xl text-parchment">Du er invitert</h1>
            <p className="text-sm text-mist">Aksepter invitasjonen for å bli med i en husholdning.</p>
          </div>

          {!token ? (
            <EmptyState
              icon="warning"
              title="Mangler invitasjons-token"
              description="Lenken er ufullstendig. Be inviteren sende den på nytt."
            />
          ) : !user ? (
            <SignInPrompt token={token} />
          ) : phase === 'accepting' ? (
            <div className="grid place-items-center gap-3 py-4 text-sm text-mist">
              <Spinner size={22} />
              Aksepterer...
            </div>
          ) : phase === 'done' ? (
            <div className="grid place-items-center gap-3 py-4 text-sm text-candle">
              <Icon name="check-circle" size={28} />
              Velkommen om bord — sender deg til kjelleren...
            </div>
          ) : (
            <EmptyState
              icon="warning"
              title="Kunne ikke akseptere"
              description={errorMsg ?? 'Lenken kan være utløpt eller allerede brukt.'}
              action={
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    iconLeft="refresh"
                    onClick={() => {
                      setPhase('accepting');
                      setErrorMsg(null);
                      accept.mutate();
                    }}
                  >
                    Prøv igjen
                  </Button>
                  <Link to="/cellar">
                    <Button variant="primary" iconRight="arrow-right">
                      Til kjelleren
                    </Button>
                  </Link>
                </div>
              }
            />
          )}
        </Card>

        <div className="mt-6 text-center text-xs text-mist">
          <Link to="/" className="inline-flex items-center gap-1 hover:text-candle">
            <Icon name="chevron-left" size={12} /> Til forsiden
          </Link>
        </div>
      </motion.div>
    </div>
  );
}

/**
 * For unauthenticated visitors. We hand the token to the login form via
 * `redirectTo` so verification redirects back to /invite/:token, where the
 * effect above will auto-accept.
 */
function SignInPrompt({ token }: { token: string }) {
  return (
    <div className="grid gap-3 text-center">
      <p className="text-sm text-parchment">Logg inn først, så binder vi invitasjonen til kontoen din.</p>
      <Link to={`/login`} state={{ from: `/invite/${token}` }}>
        <Button variant="primary" fullWidth iconRight="arrow-right">
          Logg inn for å fortsette
        </Button>
      </Link>
      <p className="text-xs text-mist">Vi sender en kode til e-posten din — ikke noe passord.</p>
      {/* Avoid unused-import error if user ever lands here without auth helpers */}
      <span className="hidden">{Auth ? '' : ''}</span>
    </div>
  );
}
